/* eslint-disable no-console */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { sendErrorReport, isFeatureEnabled } from 'shared/helpers'
import { platformFeatures } from 'shared/constants'
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  TextInput,
  PhoneNumberInput,
  TextArea
} from 'shared/components'
import { validateEmail, validateJSON, debouncedValidateJSON } from 'shared/validation'
import { addUserGroupManager } from 'src/LicenseUsers/actions'
import './styles.scss'

const AddGroupManager = ({ closeCb, confirmCb }) => {
  const isMetadataFeatureEnabled = isFeatureEnabled(platformFeatures.extra_metadata)
  const { companyId, groupID } = useParams()
  const [isLoading, setLoading] = useState(false)
  const [isDirty, setDirty] = useState(false)
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false)

  // form state
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [metadata, setMetadata] = useState('')
  const [metadataError, setMetadataError] = useState('')

  const validateMetadata = async val => {
    setLoading(true)
    let errors
    try {
      errors = await validateJSON(metadata)
      setMetadataError(errors)
    } catch (err) {
      sendErrorReport(err, 'Cannot validate, edit metadata JSON to valid format', { value: val })
    }
    setLoading(false)
    if (errors) {
      return false
    }
    return true
  }

  const isEmailValid = async (val) => {
    const emailErr = await validateEmail(val)
    setEmailError(emailErr)
    return !emailErr
  }

  const handleEmailChange = async val => {
    setDirty(true)
    setEmail(val)
    await isEmailValid(val)
  }

  const isFormValid = async () => {
    const metadataValid = await validateMetadata()
    return (await isEmailValid(email)) && metadataValid
  }

  const handleSuccess = msg => {
    confirmCb()
    Notification('success', __('Changes saved successfully'), msg)
  }

  const addUser = () => {
    const succesMsg = __('Group manager added')
    const data = {
      email,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      metadata: metadata ? JSON.parse(metadata) : {}
    }

    addUserGroupManager(companyId, groupID, data)
      .then(() => {
        setLoading(false)
        handleSuccess(succesMsg)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot add user group manager', data)
        setLoading(false)
        Notification(
          'error',
          __('Your changes were not saved'),
          __('There was an error while saving your changes')
        )
      })
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const isValid = await isFormValid()
    if (!isValid || isLoading) {
      return false
    }

    setDirty(false)
    setLoading(true)
    addUser()
    return true
  }

  const handleClose = () => {
    if (!isDirty) {
      return closeCb()
    }
    return setDirtyFormDisplay(true)
  }

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={handleClose}
      disabled={isLoading}
      title={__('Add group manager')}
    >
      <form className='AddGroupManager' onSubmit={handleSubmit}>
        <div className='form-inner'>
          <div className='left'>
            <div className='form-row'>
              <Label text={__('Email')} inputId='email' />
              <TextInput
                id='email'
                value={email}
                error={emailError}
                handleChange={handleEmailChange}
              />
            </div>
            <div className='form-row'>
              <Label text={__('First Name')} inputId='firstname' />
              <TextInput
                id='firstname'
                value={firstName}
                handleChange={val => {
                  setDirty(true)
                  setFirstName(val)
                }}
              />
            </div>
            <div className='form-row'>
              <Label text={__('Last Name')} inputId='lastname' />
              <TextInput
                id='lastname'
                value={lastName}
                handleChange={val => {
                  setDirty(true)
                  setLastName(val)
                }}
              />
            </div>
            <div className='form-row'>
              <Label text={__('Phone Number')} inputId='phone' />
              <PhoneNumberInput
                value={phoneNumber}
                handleChange={val => {
                  setDirty(true)
                  setPhoneNumber(val)
                }}
              />
            </div>
            <div className='form-row'>
              <Label inputId='metadata-input' text={__('Metadata JSON')} />
              <TextArea
                handleChange={val => {
                  setDirty(true)
                  setMetadata(val)
                  debouncedValidateJSON(val).then(err => setMetadataError(err))
                }}
                id='metadata-input'
                type='metadata'
                value={metadata}
                error={metadataError}
                rows='4'
                disabled={!isMetadataFeatureEnabled}
              />
            </div>
          </div>
        </div>
        <button type='submit' style={{ visibility: 'hidden' }} />
      </form>
      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  )
}

AddGroupManager.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired
}

export default AddGroupManager
